import { useState, useEffect } from 'react';
import { Video, ExternalLink, Calendar, Clock } from 'lucide-react';
import { lmsApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function LiveSessions() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuthStore();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const res = await lmsApi.getLiveSessions();
      setSessions(res.data);
    } catch (error) {
      toast.error('Failed to load live sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const now = new Date();
  const upcoming = sessions.filter(s => s.status === 'scheduled' && new Date(s.scheduled_at) > now);
  const past = sessions.filter(s => s.status !== 'scheduled' || new Date(s.scheduled_at) <= now);

  const getTimeUntil = (date) => {
    const diff = new Date(date) - now;
    if (diff < 0) return 'Started';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getPlatformLabel = (p) => {
    switch (p) {
      case 'zoom': return 'Zoom';
      case 'google_meet': return 'Google Meet';
      case 'teams': return 'Microsoft Teams';
      default: return 'Meeting';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Live Sessions</h1>
      <p className="text-gray-600 mb-6">Join live teaching sessions</p>

      {/* Upcoming */}
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Upcoming Sessions</h2>
      {upcoming.length === 0 ? (
        <div className="text-center py-8 mb-8 text-gray-500 bg-gray-50 rounded-lg">
          <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p>No upcoming sessions scheduled</p>
        </div>
      ) : (
        <div className="space-y-4 mb-8">
          {upcoming.map(session => (
            <div key={session.id} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                      In {getTimeUntil(session.scheduled_at)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{session.title}</h3>
                  {session.description && (
                    <p className="text-sm text-gray-500 mt-1">{session.description}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Calendar className="w-3.5 h-3.5 mr-1" />
                      {new Date(session.scheduled_at).toLocaleDateString()} at {new Date(session.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-3.5 h-3.5 mr-1" />
                      {session.duration_minutes} min
                    </span>
                    <span>{getPlatformLabel(session.meeting_platform)}</span>
                  </div>
                  {session.creator_name && (
                    <p className="text-xs text-gray-400 mt-1">By {session.creator_name}</p>
                  )}
                </div>
                <button
                  onClick={() => window.open(session.meeting_url, '_blank')}
                  className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex-shrink-0"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Join
                  <ExternalLink className="w-3 h-3 ml-1" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Past */}
      {past.length > 0 && (
        <>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Past Sessions</h2>
          <div className="space-y-2">
            {past.map(session => (
              <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-100 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-700">{session.title}</h3>
                  <p className="text-sm text-gray-500">
                    {new Date(session.scheduled_at).toLocaleDateString()} | {session.duration_minutes} min | {getPlatformLabel(session.meeting_platform)}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  session.status === 'completed' ? 'bg-green-100 text-green-700' :
                  session.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-gray-200 text-gray-600'
                }`}>{session.status}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default LiveSessions;
